import { Col, Spin } from 'antd';
import useDeclarationFooter from 'hooks/useDeclarationFooter';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeclarationFooterType } from 'store/declaration-footer/declaration-footer';
import {
    BtSubmit,
    FooterButton,
    FooterRow,
    SaveProductButton,
    Saving,
    SubmitDiv,
} from 'views/declarations/footer/Footer.styles';
import { SubmitLoading } from '../declaration-form/Form.styles';

interface Props {
    isSubmiting?: boolean;
    isSaving?: boolean;
    cancelSaveProduct: Function;
    saveProduct: Function;
    disabled?: boolean;
    saveDraft: Function;
    cancelAmendment: Function;
    submitAmendment: Function;
    /** For flows where persisting mid-amendment would corrupt what the amendment is compared against. */
    hideSaveDraft?: boolean;
    /**
     * Keeps the declaration-level actions on every tab instead of swapping to the product ones. For an
     * amendment submitted as one message, the item being edited is saved by Submit itself, so a
     * separate product footer would only hide the action the user is actually there to take.
     */
    alwaysShowDeclarationActions?: boolean;
    /** For forms that already offer a way back to the item list outside the footer. */
    hideBackToProducts?: boolean;
}

const AmendmentFooter: FC<Props> = ({
    isSubmiting = false,
    isSaving = false,
    cancelSaveProduct,
    cancelAmendment,
    disabled = false,
    saveProduct,
    saveDraft,
    submitAmendment,
    hideSaveDraft = false,
    alwaysShowDeclarationActions = false,
    hideBackToProducts = false,
}) => {
    const { declarationFooter } = useDeclarationFooter();
    const { t } = useTranslation('common');
    const [renderSave, setRenderSave] = useState(isSaving);

    useEffect(() => {
        setRenderSave(isSaving);
    }, [isSaving]);

    const footerButtons = useMemo(() => {
        return declarationFooter === DeclarationFooterType.PRODUCTS && !alwaysShowDeclarationActions ? (
            <Col>
                {renderSave && <Saving>{t('footer.saving')}</Saving>}
                {!hideBackToProducts && (
                    <FooterButton onClick={() => cancelSaveProduct()}>{t('footer.backToProducts')}</FooterButton>
                )}
                {!disabled && (
                    <SaveProductButton
                        onClick={async () => {
                            await saveProduct();
                        }}
                    >
                        {t('footer.saveProduct')}
                    </SaveProductButton>
                )}
            </Col>
        ) : (
            <Col>
                <FooterButton onClick={() => cancelAmendment()}>Cancel Amendment</FooterButton>
                {!hideSaveDraft && <FooterButton onClick={() => saveDraft()}>Save Draft</FooterButton>}
                <BtSubmit type="primary" onClick={() => submitAmendment()}>
                    {t('footer.submit')} {isSubmiting && <Spin indicator={<SubmitLoading spin />} />}
                </BtSubmit>
            </Col>
        );
    }, [
        declarationFooter,
        cancelAmendment,
        saveDraft,
        submitAmendment,
        saveProduct,
        cancelSaveProduct,
        disabled,
        isSubmiting,
        renderSave,
        hideSaveDraft,
        alwaysShowDeclarationActions,
        hideBackToProducts,
        t,
    ]);

    return (
        <>
            <SubmitDiv>
                <FooterRow justify="end" align="middle" padding="1.2rem 2.4rem 1.2rem 2.4rem">
                    {footerButtons}
                </FooterRow>
            </SubmitDiv>
        </>
    );
};

export default AmendmentFooter;
